<template>
  <b-modal
    :id="modalId"
    ref="rewardChangeHistoryModal"
    title="Reward Change History"
    size="xl"
    hide-footer
    centered
  >
    <div v-if="loading" class="text-center p-4">
      <b-spinner variant="primary" />
      <p class="mt-2">Loading change history...</p>
    </div>
    <div v-else-if="error" class="alert alert-danger">
      {{ error }}
    </div>
    <div v-else-if="!changeData || (!changeData.all_changes || changeData.all_changes.length === 0)" class="text-center p-4">
      <p>No change history found for this reward.</p>
    </div>
    <div v-else>
      <b-tabs>
        <b-tab title="All Changes" active>
          <b-table
            :items="changeData.all_changes"
            :fields="historyFields"
            small
            responsive
            striped
            hover
            class="mt-3"
          >
            <template #cell(change_type)="data">
              <b-badge :variant="getChangeTypeVariant(data.value)">
                {{ formatChangeType(data.value) }}
              </b-badge>
            </template>
            <template #cell(old_value)="data">
              <span v-if="data.value !== null && data.value !== undefined">
                {{ formatValue(data.value, data.item.change_type) }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>
            <template #cell(new_value)="data">
              <span v-if="data.value !== null && data.value !== undefined">
                {{ formatValue(data.value, data.item.change_type) }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>
            <template #cell(old_payout_amount)="data">
              <span v-if="data.value !== null && data.value !== undefined" class="text-nowrap">
                {{ data.value | formatCurrency }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>
            <template #cell(new_payout_amount)="data">
              <span v-if="data.value !== null && data.value !== undefined" class="text-nowrap">
                {{ data.value | formatCurrency }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>
            <template #cell(changed_at)="data">
              <span class="text-nowrap">{{ data.value | dotDateTime }}</span>
            </template>
            <template #cell(changed_by)="data">
              <UserBadgeName
                v-if="data.item.changed_by_admin_name"
                :name="data.item.changed_by_admin_name"
                :color="data.item.changed_by_admin_hex_color"
              />
              <span v-else-if="data.value">
                {{ typeof data.value === 'object' ? (data.value.name || data.value.id || '-') : data.value }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>
          </b-table>
        </b-tab>
        <b-tab title="Deactivations">
          <b-table
            v-if="changeData.deactivations && changeData.deactivations.length > 0"
            :items="changeData.deactivations"
            :fields="historyFields"
            small
            responsive
            striped
            hover
            class="mt-3"
          >
            <template #cell(change_type)="data">
              <b-badge :variant="getChangeTypeVariant(data.value)">
                {{ formatChangeType(data.value) }}
              </b-badge>
            </template>
            <template #cell(old_value)="data">
              <span v-if="data.value !== null && data.value !== undefined">
                {{ formatValue(data.value, data.item.change_type) }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>
            <template #cell(new_value)="data">
              <span v-if="data.value !== null && data.value !== undefined">
                {{ formatValue(data.value, data.item.change_type) }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>
            <template #cell(old_payout_amount)="data">
              <span v-if="data.value !== null && data.value !== undefined" class="text-nowrap">
                {{ data.value | formatCurrency }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>
            <template #cell(new_payout_amount)="data">
              <span v-if="data.value !== null && data.value !== undefined" class="text-nowrap">
                {{ data.value | formatCurrency }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>
            <template #cell(changed_at)="data">
              <span class="text-nowrap">{{ data.value | dotDateTime }}</span>
            </template>
            <template #cell(changed_by)="data">
              <UserBadgeName
                v-if="data.item.changed_by_admin_name"
                :name="data.item.changed_by_admin_name"
                :color="data.item.changed_by_admin_hex_color"
              />
              <span v-else-if="data.value">
                {{ typeof data.value === 'object' ? (data.value.name || data.value.id || '-') : data.value }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>
          </b-table>
          <div v-else class="text-center p-4">
            <p>No deactivations found.</p>
          </div>
        </b-tab>
        <b-tab title="Payout Changes">
          <b-table
            v-if="changeData.payout_changes && changeData.payout_changes.length > 0"
            :items="changeData.payout_changes"
            :fields="historyFields"
            small
            responsive
            striped
            hover
            class="mt-3"
          >
            <template #cell(change_type)="data">
              <b-badge :variant="getChangeTypeVariant(data.value)">
                {{ formatChangeType(data.value) }}
              </b-badge>
            </template>
            <template #cell(old_value)="data">
              <span v-if="data.value !== null && data.value !== undefined">
                {{ formatValue(data.value, data.item.change_type) }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>
            <template #cell(new_value)="data">
              <span v-if="data.value !== null && data.value !== undefined">
                {{ formatValue(data.value, data.item.change_type) }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>
            <template #cell(old_payout_amount)="data">
              <span v-if="data.value !== null && data.value !== undefined" class="text-nowrap">
                {{ data.value | formatCurrency }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>
            <template #cell(new_payout_amount)="data">
              <span v-if="data.value !== null && data.value !== undefined" class="text-nowrap">
                {{ data.value | formatCurrency }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>
            <template #cell(changed_at)="data">
              <span class="text-nowrap">{{ data.value | dotDateTime }}</span>
            </template>
            <template #cell(changed_by)="data">
              <UserBadgeName
                v-if="data.item.changed_by_admin_name"
                :name="data.item.changed_by_admin_name"
                :color="data.item.changed_by_admin_hex_color"
              />
              <span v-else-if="data.value">
                {{ typeof data.value === 'object' ? (data.value.name || data.value.id || '-') : data.value }}
              </span>
              <span v-else class="text-muted">-</span>
            </template>
          </b-table>
          <div v-else class="text-center p-4">
            <p>No payout changes found.</p>
          </div>
        </b-tab>
      </b-tabs>
    </div>
  </b-modal>
</template>

<script>
import UserBadgeName from '@/views/users/UserBadgeName.vue'

export default {
  name: 'RewardChangeHistoryModal',
  components: {
    UserBadgeName
  },
  props: {
    rewardId: {
      type: Number,
      default: null
    },
    modalId: {
      type: String,
      default: 'reward-change-history-modal'
    }
  },
  data () {
    return {
      changeData: null,
      loading: false,
      error: null,
      historyFields: [
        { key: 'change_type', label: 'Change Type', sortable: false },
        { key: 'old_value', label: 'Old Value', sortable: false },
        { key: 'new_value', label: 'New Value', sortable: false },
        { key: 'old_payout_amount', label: 'Old Payout', sortable: false },
        { key: 'new_payout_amount', label: 'New Payout', sortable: false },
        { key: 'changed_at', label: 'Changed At', sortable: false },
        { key: 'changed_by', label: 'Changed By', sortable: false }
      ]
    }
  },
  watch: {
    rewardId: {
      handler (newVal) {
        if (newVal && this.$refs.rewardChangeHistoryModal && this.$refs.rewardChangeHistoryModal.isVisible) {
          this.fetchChangeHistory()
        }
      },
      immediate: false
    }
  },
  methods: {
    async fetchChangeHistory () {
      if (!this.rewardId) {
        this.error = 'Reward ID is required'
        return
      }

      this.loading = true
      this.error = null
      this.changeData = null

      try {
        const response = await this.$http.get(`/api/offer-events/rewards/${this.rewardId}/change-history`)
        this.changeData = response.data || {}
      } catch (error) {
        this.error = error.response?.data?.message || 'Failed to fetch change history'
        this.changeData = null
      } finally {
        this.loading = false
      }
    },
    show () {
      this.fetchChangeHistory()
      this.$refs.rewardChangeHistoryModal.show()
    },
    hide () {
      this.$refs.rewardChangeHistoryModal.hide()
    },
    formatChangeType (changeType) {
      if (!changeType) return 'Unknown'
      // Convert snake_case to Title Case
      return changeType
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    formatValue (value, changeType) {
      // Only status changes carry the is_active boolean; render those as Active/Inactive.
      if (changeType === 'status_activate' || changeType === 'status_deactivate') {
        if (value === '1' || value === 1) return 'Active'
        if (value === '0' || value === 0) return 'Inactive'
      }
      return value
    },
    getChangeTypeVariant (changeType) {
      if (!changeType) return 'secondary'
      if (changeType.includes('deactivate') || changeType.includes('inactive')) {
        return 'danger'
      }
      if (changeType.includes('activate') || changeType.includes('active')) {
        return 'success'
      }
      if (changeType.includes('payout') || changeType.includes('reward')) {
        return 'warning'
      }
      return 'info'
    }
  }
}
</script>

<style scoped>
</style>
